import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Facebook, Instagram, Linkedin, ChevronRight, Mail, Phone, MapPin } from 'lucide-react';

export default function Footer() {
  const { t } = useLanguage();

  const maluzLinks = [
    { label: 'Home', to: '/' },
    { label: t('nav.about'), to: '/#about' },
    { label: t('nav.services'), to: '/#services' },
    { label: t('nav.industries'), to: '/#industries' },
    { label: t('nav.contact'), to: '/#contact-form' },
  ];

  const products = [
    { label: 'Microsoft', to: '/licenses' },
    { label: 'Adobe', to: '/licenses' },
    { label: 'Kaspersky', to: '/licenses' },
    { label: 'Ekahau', to: '/licenses' },
    { label: '3CX', to: '/licenses' },
  ];

  const servicesList = [
    t('services.licenses.title'),
    t('services.support.title'),
    t('services.procurement.title'),
    t('services.hosting.title'),
    'Apps Development',
  ];

  const solutionsList = [
    'Microsoft 365 & Azure',
    'Adobe Creative Cloud',
    'Kaspersky Endpoint Security',
    'Ekahau Wi-Fi Solutions',
    '3CX Communications',
    'Procurement Industrial',
    'Web Hosting',
  ];

  const linkStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: 'rgba(255,255,255,0.6)', lineHeight: 1.8, transition: 'color 0.2s', cursor: 'pointer', textDecoration: 'none' };
  const headingStyle: React.CSSProperties = { fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700, color: 'white', marginBottom: 20, position: 'relative' as const, paddingBottom: 16 };
  const dividerStyle: React.CSSProperties = { position: 'absolute' as const, bottom: 0, left: 0, width: 40, height: 2, background: 'var(--primary-light, #42A5F5)' };

  return (
    <footer id="footer" data-testid="footer-section" style={{ background: '#0C1829', color: 'white' }}>
      {/* Watermark */}
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '64px 24px 0' }}>
        <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 'clamp(40px, 8vw, 100px)', lineHeight: 0.95, letterSpacing: '-0.04em', color: 'rgba(255,255,255,0.04)', marginBottom: 48 }}>
          MALUZ<br />TECHNOLOGIES
        </div>
      </div>

      {/* Main footer grid */}
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px 48px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 40 }} className="footer-grid">
          {/* Col 1: Contactos */}
          <div>
            <h3 style={headingStyle}>Contactos<span style={dividerStyle} /></h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <a href="tel:+244226438350" style={linkStyle} data-testid="footer-phone-1"
                onMouseEnter={e => (e.currentTarget.style.color = 'white')} onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.6)')}>
                <Phone size={14} style={{ flexShrink: 0 }} /> +244 226 438 350
              </a>
              <a href="mailto:sales@maluzts.com" style={linkStyle} data-testid="footer-email"
                onMouseEnter={e => (e.currentTarget.style.color = 'white')} onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.6)')}>
                <Mail size={14} style={{ flexShrink: 0 }} /> sales@maluzts.com
              </a>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              {[
                { icon: Facebook, href: 'https://www.facebook.com/MaluzTechnologies' },
                { icon: Instagram, href: 'https://www.instagram.com/maluztechnologies/' },
                { icon: Linkedin, href: 'https://www.linkedin.com/company/maluz-technologies' },
              ].map((s, i) => {
                const Icon = s.icon;
                return (
                  <a key={i} href={s.href} target="_blank" rel="noopener noreferrer"
                    style={{ width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.08)', borderRadius: '50%', color: 'rgba(255,255,255,0.5)', transition: 'all 0.2s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--primary)'; e.currentTarget.style.color = 'white'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}>
                    <Icon size={15} />
                  </a>
                );
              })}
            </div>
            <div style={{ marginTop: 24 }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, color: 'white', marginBottom: 10 }}>{t('contact.address')}</h4>
              <div style={{ width: 30, height: 2, background: 'var(--primary-light, #42A5F5)', marginBottom: 10 }} />
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7 }} data-testid="footer-address">
                <MapPin size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
                Bloco 10, Predio 12, Apt. 301,<br />Cidade do Sequele, Angola
              </p>
            </div>
          </div>

          {/* Col 2: MALUZ TECHNOLOGIES */}
          <div>
            <h3 style={headingStyle}>MALUZ TECHNOLOGIES<span style={dividerStyle} /></h3>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {maluzLinks.map((link, i) => (
                <Link key={i} to={link.to} style={linkStyle}
                  onMouseEnter={e => (e.currentTarget.style.color = 'white')}
                  onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.6)')}>
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Col 3: Produtos */}
          <div>
            <h3 style={headingStyle}>Produtos<span style={dividerStyle} /></h3>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {products.map((p, i) => (
                <Link key={i} to={p.to} style={linkStyle}
                  onMouseEnter={e => (e.currentTarget.style.color = 'white')}
                  onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.6)')}>
                  <ChevronRight size={12} style={{ flexShrink: 0 }} /> {p.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Col 4: Serviços */}
          <div>
            <h3 style={headingStyle}>{t('nav.services')}<span style={dividerStyle} /></h3>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {servicesList.map((s, i) => (
                <span key={i} style={linkStyle}>
                  <ChevronRight size={12} style={{ flexShrink: 0 }} /> {s}
                </span>
              ))}
            </div>
          </div>

          {/* Col 5: Soluções */}
          <div>
            <h3 style={headingStyle}>Soluções<span style={dividerStyle} /></h3>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {solutionsList.map((s, i) => (
                <span key={i} style={{ ...linkStyle, fontSize: 13 }}>
                  <ChevronRight size={12} style={{ flexShrink: 0 }} /> {s}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              { Icon: Facebook, href: 'https://www.facebook.com/MaluzTechnologies' },
              { Icon: Instagram, href: 'https://www.instagram.com/maluztechnologies/' },
              { Icon: Linkedin, href: 'https://www.linkedin.com/company/maluz-technologies' },
            ].map((s, i) => (
              <a key={i} href={s.href} target="_blank" rel="noopener noreferrer"
                style={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.06)', borderRadius: '50%', color: 'rgba(255,255,255,0.4)', transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--primary)'; e.currentTarget.style.color = 'white'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; }}>
                <s.Icon size={13} />
              </a>
            ))}
          </div>
          <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>
            Copyright &copy; {new Date().getFullYear()} Maluz Technologies. {t('footer.rights')}
          </p>
        </div>
      </div>

      <style>{`
        @media (max-width: 1024px) { .footer-grid { grid-template-columns: repeat(3, 1fr) !important; } }
        @media (max-width: 768px) { .footer-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 480px) { .footer-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </footer>
  );
}
